import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { VolumeViewerDialogComponent } from '../../../../../shared/components/volume-viewer-3d/volume-viewer-dialog.component';
import { formatVolumeLabel, isVolumeFeature } from '../../../../../shared/helpers/volume-geometry';
import { formatArea } from '../../../../../shared/helpers/area-format';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import * as fromRoot from '../../../../../state/app.state';
import { projectDetails } from '../../../core/projects.selectors';
import { projectAlignments } from '../../../../../shared/helpers/alignment-stationing';
import { StationingPreferenceService } from '../../../../../shared/services/stationing-preference.service';
import { MapService } from '../../../../../shared/services/map.service';
import {
  ProjectSpatialSummaryService,
  SpatialSummaryState,
} from '../../../../../shared/services/project-spatial-summary.service';
import { MAP_VIEW_BY_COLORS, LAYER_DEFAULTS } from '../map.constants';
import { DEFAULT_MAP_TOOLBAR_STATE, LabelByOption, MapToolbarState, SwipePresetId } from '../../../../../shared/types/map.types';
import { groupReferenceLayers, hasPublishedGroups } from '../../../../../shared/helpers/reference-layer-groups';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatDatepicker } from '@angular/material/datepicker';
import { CalendarHeaderComponent } from 'src/app/shared/components/calendar-header/calendar-header.component';
import { LINKED_ONLY_FILTER, LinkedOnlyFilterGate } from '../../../../../shared/helpers/design-context';
import { DesignContextAvailabilityService } from '../../../../../shared/services/design-context-availability.service';
// DateAdapter is provided at ProjectsModule level for proper title case formatting

interface FilterItem {
  id: string;
  label?: string;  // Used by pay apps
  item?: string;  // Used by bid items
  description?: string;  // Used by bid items
}

export interface Inspector {
  id: string;
  name: string;
  email?: string;
  color?: string;
}

@Component({
    selector: 'app-map-sidenav',
    templateUrl: './map-sidenav.component.html',
    styleUrls: ['./map-sidenav.component.scss'],
    standalone: false
})
export class MapSidenavComponent implements OnInit, OnDestroy {
  // Shared Figma-styled calendar header (chevrons + white-chip tooltip).
  public calendarHeader = CalendarHeaderComponent;
  @Input() set projectId(projectId: string) {
    this._projectId = projectId;
    void this.linkedOnlyGate.check(projectId, () => this.selectedTypeFilters).then(filters => {
      if (filters) {
        this.selectedTypeFilters = filters;
        this.typeFilterChange.emit([...filters]);
      }
      this.cdr.markForCheck();
    });
  }
  get projectId(): string { return this._projectId; }
  private _projectId: string;
  /** Spec §10: the linked-only Types filter shows only for a project with a published design catalog. */
  private readonly designContextAvailability = inject(DesignContextAvailabilityService);
  private readonly linkedOnlyGate = new LinkedOnlyFilterGate(projectId => this.designContextAvailability.available(projectId));
  get linkedOnlyAvailable(): boolean { return this.linkedOnlyGate.visible; }
  @Input() set filteredSites(sites: any[]) {
    this.filteredSitesForView = sites || [];
    // Build aggregated dates when filtered sites change
    this.buildAggregatedDates();
  }
  @Input() set availableSites(sites: Array<{id: string, name: string}>) {
    // Update site filters when input changes
    if (sites && sites.length > 0) {
      this.siteFilters = sites.map(site => ({
        id: site.id,
        label: site.name
      }));
      this.hasSingleSite = sites.length === 1;
    } else {
      this.siteFilters = [];
      this.hasSingleSite = false;
    }
  }
  @Input() set availableBidItems(items: Array<{id: string, item: string, description: string}>) {
    // Update bid item filters when input changes
    if (items && items.length > 0) {
      this.bidItemFilters = items.map(item => ({
        id: item.id,  // ID is already a string (base58)
        item: item.item,
        description: item.description
      }));
    }
  }

  // Bid items with geometries in current date range (for View By Bid Item list)
  @Input() set bidItemsWithGeometries(items: Array<{id: string, item: string, description: string}>) {
    this._bidItemsWithGeometries = items || [];
    this.buildAggregatedBidItems();
  }
  get bidItemsWithGeometries() {
    return this._bidItemsWithGeometries;
  }
  private _bidItemsWithGeometries: Array<{id: string, item: string, description: string}> = [];

  // Selected bid item filter (empty array = all selected)
  @Input() set selectedBidItemIds(ids: string[]) {
    this._selectedBidItemIds = ids || [];
    this.buildAggregatedBidItems();
  }
  get selectedBidItemIds() {
    return this._selectedBidItemIds;
  }
  private _selectedBidItemIds: string[] = [];

  // Single day mode (for daily report map)
  @Input() isSingleDayMode: boolean = false;

  // Available inspectors (for single day mode)
  @Input() set availableInspectors(inspectors: Inspector[]) {
    this._availableInspectors = inspectors || [];
    this.buildAggregatedInspectors();
  }
  get availableInspectors() {
    return this._availableInspectors;
  }
  private _availableInspectors: Inspector[] = [];

  // Selected date (for single day mode)
  @Input() set selectedDate(date: Date) {
    if (date) {
      this._selectedDate = date;
      this.singleDateControl.setValue(date, { emitEvent: false });
    }
  }
  get selectedDate() {
    return this._selectedDate;
  }
  private _selectedDate: Date = new Date();

  // Layer settings (synced from container)
  @Input() set layerSettings(settings: { siteBoundary: boolean; picture: boolean; asBuilt: boolean; bidItem?: boolean; activity?: boolean; delivery?: boolean }) {
    if (settings) {
      this.selectedLayers = {
        siteBoundary: settings.siteBoundary,
        picture: settings.picture,
        asBuilt: settings.asBuilt
      };
    }
  }

  // Selected type filters (synced from container)
  @Input() set initialTypeFilters(filters: string[]) {
    if (filters) {
      this.selectedTypeFilters = [...filters];
    }
  }

  @Output() searchChange = new EventEmitter<string>();
  @Output() dateRangeChange = new EventEmitter<any>();
  @Output() dateChange = new EventEmitter<Date>(); // For single day mode
  @Output() viewModeChange = new EventEmitter<string>();
  @Output() viewByChange = new EventEmitter<'site' | 'bid_item' | 'day' | 'inspector'>();
  @Output() siteHover = new EventEmitter<string | null>();
  @Output() bidItemHover = new EventEmitter<string | null>();
  @Output() inspectorHover = new EventEmitter<string | null>(); // For single day mode
  @Output() dateHover = new EventEmitter<string | null>();
  @Output() layerChange = new EventEmitter<any>();
  @Output() typeFilterChange = new EventEmitter<string[]>();
  @Output() bidItemFilterChange = new EventEmitter<string[]>();
  @Output() siteFilterChange = new EventEmitter<string[]>();

  // Label outputs
  @Output() labelToggle = new EventEmitter<void>();
  @Output() labelByChange = new EventEmitter<LabelByOption>();

  // Label state
  @Input() labelsEnabled = false;
  @Input() labelBy: LabelByOption = 'bid_item_name';

  // Design tab: the project's published design layers, beside the SiteNotes
  // layers, so the View panel is the one place for what the map draws. The
  // host passes the map's toolbar state and forwards these outputs to the map.
  @Input() showDesignTab = false;
  /** Active tab of the View dropdown, remembered across reopen. */
  viewTabIndex = 0;
  @Input() projectLayers: MapToolbarState = { ...DEFAULT_MAP_TOOLBAR_STATE };
  @Input() canManageLayers = false;
  @Output() projectLayerToggle = new EventEmitter<string>();
  @Output() projectLayerOpacity = new EventEmitter<{ id: string; opacity: number }>();
  @Output() projectLayerLabels = new EventEmitter<string>();
  @Output() projectLayerZoom = new EventEmitter<string>();
  @Output() projectLayersReset = new EventEmitter<void>();
  @Output() projectLayersRetry = new EventEmitter<void>();
  @Output() projectLayerSwipe = new EventEmitter<SwipePresetId>();
  designSearch = '';
  get designGroups() { return groupReferenceLayers(this.projectLayers, this.designSearch); }
  get designHasGroups(): boolean { return hasPublishedGroups(this.projectLayers); }
  get designVisibleCount(): number { return this.projectLayers.visibleOverlayIds.length; }
  isDesignVisible(id: string): boolean { return this.projectLayers.visibleOverlayIds.includes(id); }
  setDesignSearch(event: Event): void { this.designSearch = (event.target as HTMLInputElement).value; }
  changeDesignOpacity(id: string, event: Event): void {
    this.projectLayerOpacity.emit({ id, opacity: Number((event.target as HTMLInputElement).value) / 100 });
  }
  opacityPercent(opacity: number | undefined): number { return Math.round((opacity ?? 1) * 100); }
  /** A placed drawing's row: the revision it shows, in the project's Drawings tab (leaving the fullscreen map). */
  openDrawingSheet(revisionUuid: string): void {
    void this.router.navigate(['/projects', this.projectId],
      { queryParams: { fs: null, tab: 'drawings', sheet: null, set: null, revision: revisionUuid }, queryParamsHandling: 'merge' });
  }

  // Sticky selection outputs (multi-select support)
  @Output() siteSelectionChange = new EventEmitter<Set<string>>();
  @Output() bidItemSelectionChange = new EventEmitter<Set<string>>();
  @Output() inspectorSelectionChange = new EventEmitter<Set<string>>();
  @Output() dateSelectionChange = new EventEmitter<Set<string>>();

  projectName = '';
  searchQuery = '';
  selectedViewMode = 'sites';
  showFiltersOverlay = false;
  showViewOverlay = false;
  filtersOverlayOrigin: 'icon' | 'chip' = 'icon'; // Track which button opened the overlay

  // View dropdown state
  selectedColorBy: string = 'site'; // 'site' | 'day' | 'week' | 'payapp' - default to 'site'
  selectedViewBy: 'site' | 'bid_item' | 'day' | 'inspector' = 'site'; // View by: site (default), bid_item, day, inspector
  hoveredSiteId: string | null = null;
  hoveredBidItemId: string | null = null;
  hoveredDate: string | null = null;

  // Sticky selection state (multi-select - users can click to lock highlight on multiple items)
  selectedSiteIdsForHighlight = new Set<string>();
  selectedBidItemIdsForHighlight = new Set<string>();
  selectedInspectorIdsForHighlight = new Set<string>();
  selectedDatesForHighlight = new Set<string>();

  // Aggregated bid items for the list (bid items with geometries in current date range)
  aggregatedBidItems: Array<{
    id: string;
    item: string;
    description: string;
    color: string;
  }> = [];

  // Aggregated inspectors for the list (inspectors with reports in selected date)
  aggregatedInspectors: Inspector[] = [];
  // Aggregated dates for the list (dates with reports in current date range)
  aggregatedDates: Array<{
    date: string;
    displayDate: string;
    color: string;
  }> = [];

  /** 7.2: the Stationing row shows for a project with alignments */
  hasStationing = false;
  stationingOn = true;
  private stationingProjectId: string | null = null;
  private stationingSub: Subscription | null = null;
  private readonly stationingPreference = inject(StationingPreferenceService);

  private syncStationing(project: { id?: string; alignment_geometry?: unknown } | null): void {
    this.hasStationing = !!project?.id && projectAlignments(project.alignment_geometry).length > 0;
    const id = this.hasStationing ? project.id : null;
    if (id === this.stationingProjectId) return;
    this.stationingProjectId = id;
    this.stationingSub?.unsubscribe();
    this.stationingSub = id ? this.stationingPreference.visible$(id).subscribe(on => { this.stationingOn = on; }) : null;
  }

  // Layer state properties - using centralized defaults for fullscreen mode
  selectedLayers = {
    siteBoundary: LAYER_DEFAULTS.fullscreen.siteBoundary,
    picture: LAYER_DEFAULTS.fullscreen.picture,
    asBuilt: LAYER_DEFAULTS.fullscreen.asBuilt
  };

  // Prefix used to encode control-form group toggles into selectedTypeFilters
  // (kept in sync with map-layer-container's FORM_GROUP_PREFIX).
  private readonly FORM_GROUP_PREFIX = 'form_group:';

  // Base type filters (bid item / delivery / activities). Control forms are
  // rendered as one dynamic toggle PER GROUP (see availableFormGroups), not as a
  // single flat 'forms' entry.
  baseTypeFilters = [
    { id: 'bidItem', label: 'Bid item', icon: 'type-bid-item' },
    { id: 'delivery', label: 'Deliveries', icon: 'type-delivery' },
    { id: 'activities', label: 'Activities', icon: 'type-activities' }
  ];

  /**
   * Spec §7.3 map filter: a display token, not a layer (present = only records with a design
   * context link). Rendered after the type toggles and kept out of baseTypeFilters, which also
   * drives the all-checked defaults.
   */
  readonly linkedOnlyFilter = { id: LINKED_ONLY_FILTER, label: 'Only records linked to design features', icon: 'marker-outline' };

  // Single flat Forms toggle — single-day ONLY. The multi-day map uses dynamic
  // per-group toggles (formGroupMode) and must NEVER show this flat fallback.
  private readonly flatFormsTypeFilter = { id: 'forms', label: 'Forms', icon: 'type-quality' };

  // Set true by the multi-day container so the Types tab always uses dynamic
  // per-group form toggles and never falls back to the flat 'Forms' toggle —
  // even during the brief window before availableFormGroups is populated.
  @Input() formGroupMode = false;

  // Dynamic control-form group toggles (driven by ui_config.map_display.group).
  // Each becomes a Types-tab toggle whose id is `form_group:<groupId>`. Group
  // toggles default UNCHECKED and are shown IFF checked — exactly like the base
  // types (token present = shown, absent = hidden). The glyph is resolved upstream
  // (MapSymbolFactory.resolveControlFormTypeGlyph) to a standard grey type-* glyph
  // paired to the form's map_display.icon; falls back to 'type-quality'.
  @Input() set availableFormGroups(groups: Array<{ id: string; label: string; icon: string | null }>) {
    this._availableFormGroups = (groups || []).map(g => ({
      id: `${this.FORM_GROUP_PREFIX}${g.id}`,
      label: g.label,
      icon: g.icon || 'type-quality',
    }));
  }
  get availableFormGroups() {
    return this._availableFormGroups as any;
  }
  private _availableFormGroups: Array<{ id: string; label: string; icon: string }> = [];

  // Combined list rendered in the Types tab.
  // - formGroupMode (multi-day): base types + dynamic per-group toggles only.
  //   NEVER the flat 'Forms' toggle. If groups aren't published yet, show just
  //   the base types until they arrive (no flicker to a flat all-checked Forms).
  // - single-day: base types + the single flat 'Forms' toggle.
  get typeFilters(): Array<{ id: string; label: string; icon: string }> {
    if (this.formGroupMode) {
      return [...this.baseTypeFilters, ...this._availableFormGroups];
    }
    if (this._availableFormGroups.length > 0) {
      return [...this.baseTypeFilters, ...this._availableFormGroups];
    }
    return [...this.baseTypeFilters, this.flatFormsTypeFilter];
  }

  selectedTypeFilters: string[] = [];

  // Site color palette (shared across all View By modes)
  siteColors = MAP_VIEW_BY_COLORS;

  filteredSitesForView: any[] = [];

  // Filter data
  siteFilters: FilterItem[] = []; // Populated from availableSites input
  bidItemFilters: FilterItem[] = []; // Populated from availableBidItems input
  hasSingleSite = false; // Track if project has only 1 site to hide filter

  payAppFilters: FilterItem[] = [
    { id: 'pay1', label: 'Pay name 1 ((July 1-14, 2024)' },
    { id: 'pay2', label: 'Pay name 2 (June 15-30, 2024)' },
    { id: 'pay3', label: 'Pay name 3 (June 1-15, 2024)' },
    { id: 'pay4', label: 'Pay name 4 (June 1-15, 2024)' },
  ];

  selectedSiteFilters: string[] = ['all'];
  selectedBidItemFilters: string[] = ['all'];
  selectedPayAppFilters: string[] = ['all'];

  // Date range picker
  @ViewChild('dateRangePicker') dateRangePicker: MatDateRangePicker<Date>;
  dateRangeForm = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
  });

  // Single date picker (for single day mode)
  @ViewChild('singleDatePicker') singleDatePicker: MatDatepicker<Date>;
  singleDateControl = new FormControl<Date | null>(null);

  // Timeline slider state
  timelineStartDate: Date;
  timelineEndDate: Date;
  selectedStartDate: Date;
  selectedEndDate: Date;
  private isDragging = false;
  dragHandle: 'start' | 'end' | 'range' | null = null; // Public for template binding
  private dragStartX = 0;
  private dragStartPercent = 0;
  projectStartDate: Date | null = null;
  private lastEmitTime = 0;
  private readonly EMIT_THROTTLE_MS = 100; // Throttle to max 10 updates per second

  // Play animation state
  isPlaying = false;
  private playIntervalId: any = null;
  private readonly BASE_PLAY_INTERVAL_MS = 300; // Base interval: 300ms per day at 1x speed
  private readonly PLAY_STEP_DAYS = 1; // Move forward by 1 day each step
  private readonly DATA_FETCH_INTERVAL = 3; // Fetch data every 3 visual steps
  private playStepCounter = 0; // Counter for data fetch throttling

  // Speed control
  playbackSpeed = 1; // Current speed multiplier (0.5x, 1x, 2x, 4x)
  private readonly SPEED_OPTIONS = [0.5, 1, 2, 4]; // Available speed multipliers
  private currentSpeedIndex = 1; // Index in SPEED_OPTIONS (starts at 1x)

  // Detail view mode state
  detailViewMode: 'none' | 'bid_item' | 'activity' | 'delivery' | 'forms' | 'picture' = 'none';
  detailViewData: any = null;
  private readonly volumeDialog = inject(MatDialog);

  /**
   * The volume capture behind the open bid-item detail (highlighted feature if it is a volume,
   * otherwise the first volume feature), so the map can offer a 3D control next to the zoom stack.
   */
  get volumeFeature3d(): { coordinates: number[][]; label: string; title: string } | null {
    if (this.detailViewMode !== 'bid_item') return null;
    const data = this.detailViewData?.data;
    const features: any[] = data?.geometry?.features || [];
    if (!features.length) return null;
    const idx = this.detailViewData?.highlightFeatureIndex;
    const feature = (idx != null && isVolumeFeature(features[idx])) ? features[idx] : features.find(f => isVolumeFeature(f));
    if (!feature?.geometry?.coordinates) return null;
    return {
      coordinates: feature.geometry.coordinates,
      label: formatVolumeLabel(feature, data.bid_item),
      title: [data.bid_item?.item, data.bid_item?.description].filter(Boolean).join(' '),
    };
  }

  openVolume3d(): void {
    const v = this.volumeFeature3d;
    if (v) VolumeViewerDialogComponent.open(this.volumeDialog, v);
  }

  private readonly onDestroy: Subject<any> = new Subject<any>();
  private searchSubject: Subject<string> = new Subject<string>();
  debouncedSearchQuery = '';

  // Spatial summary state — populated when the user draws a polygon via the
  // toolbar's spatial-select tool. Null when the tool isn't active.
  spatialSummaryState: SpatialSummaryState | null = null;

  formatSpatialArea(acres: number): string {
    return formatArea(acres);
  }
  formatSpatialQty(n: number): string {
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  formatSpatialCurrency(n: number | null): string {
    if (n == null) return '—';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  formatSpatialPct(pct: number | null): string {
    if (pct == null) return '';
    return Math.round(pct * 100) + '%';
  }

  /** Download the area summary as a PDF (map screenshot + bid item table). */
  exportSpatialSummaryPdf(): void {
    this.projectSpatialSummaryService.requestExport();
  }

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private mapService: MapService,
    private cdr: ChangeDetectorRef,
    private projectSpatialSummaryService: ProjectSpatialSummaryService,
  ) {
    // Timeline range will be calculated dynamically in ngOnInit
    // after we have access to query params and project details
  }

  ngOnInit() {
    // Subscribe to the shared spatial-summary state so the sidenav can swap
    // in an area-summary view whenever the user is drawing a polygon.
    this.projectSpatialSummaryService.state$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(state => {
        // While drilled into an item the detail panel swaps its body for the
        // summary (scoped to that item by the map component) — see template.
        this.spatialSummaryState = state.active ? state : null;
        this.cdr.markForCheck();
      });

    // In single day mode, enable all type filters by default and default to inspector view
    if (this.isSingleDayMode) {
      this.selectedTypeFilters = ['bidItem', 'activities', 'delivery', 'forms'];
      this.selectedViewBy = 'inspector';
    }

    // Initialize dates from query params first, before subscribing
    const qp = this.route.snapshot.queryParams;

    if (qp.startDate && moment(qp.startDate).isValid()) {
      this.selectedStartDate = moment(qp.startDate).toDate();
    } else {
      // Default to last 2 weeks only if no query param
      this.selectedStartDate = new Date();
      this.selectedStartDate.setDate(new Date().getDate() - 14);
    }

    if (qp.endDate && moment(qp.endDate).isValid()) {
      this.selectedEndDate = moment(qp.endDate).toDate();
    } else {
      // Default to today only if no query param
      this.selectedEndDate = new Date();
    }

    // Set up debounced search (300ms delay)
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.onDestroy)
      )
      .subscribe(query => {
        this.debouncedSearchQuery = query;
      });

    // Subscribe to project details to get project name and start date
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        if (project && project.name) {
          this.projectName = project.name;
        }
        this.syncStationing(project);

        // Get project start date for timeline calculation
        if (project && project.start_date && moment(project.start_date).isValid()) {
          this.projectStartDate = moment(project.start_date).toDate();
        }

        // Calculate timeline range after we have project details
        this.calculateTimelineRange();
      });

    // Subscribe to query params to sync calendar picker with slider (for future updates)
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        let datesChanged = false;

        if (qp.startDate && moment(qp.startDate).isValid()) {
          const newStartDate = moment(qp.startDate).toDate();
          if (this.selectedStartDate.getTime() !== newStartDate.getTime()) {
            this.selectedStartDate = newStartDate;
            // Sync form with query params
            this.dateRangeForm.controls.startDate.setValue(newStartDate, { emitEvent: false });
            datesChanged = true;
          }
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          const newEndDate = moment(qp.endDate).toDate();
          if (this.selectedEndDate.getTime() !== newEndDate.getTime()) {
            this.selectedEndDate = newEndDate;
            // Sync form with query params
            this.dateRangeForm.controls.endDate.setValue(newEndDate, { emitEvent: false });
            datesChanged = true;
          }
        }

        // Recalculate timeline range if dates changed (to accommodate dates outside default range)
        if (datesChanged) {
          this.calculateTimelineRange();
        }
      });

    // Initialize date range form with current selected dates
    this.dateRangeForm.controls.startDate.setValue(this.selectedStartDate, { emitEvent: false });
    this.dateRangeForm.controls.endDate.setValue(this.selectedEndDate, { emitEvent: false });

    // Subscribe to date range picker changes
    this.dateRangeForm.controls.endDate.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(endDate => {
        const startDate = this.dateRangeForm.controls.startDate.value;
        if (startDate && endDate && moment(startDate).isValid() && moment(endDate).isValid()) {
          // Convert to native Date objects (picker may return Moment or other date types)
          this.selectedStartDate = moment(startDate).toDate();
          this.selectedEndDate = moment(endDate).toDate();

          // Recalculate timeline range to accommodate dates outside default range
          this.calculateTimelineRange();

          // Update URL params
          this.router.navigate([], {
            queryParams: {
              startDate: moment(startDate).format('YYYY-MM-DD'),
              endDate: moment(endDate).format('YYYY-MM-DD'),
            },
            queryParamsHandling: 'merge',
          });

          // Emit date range change
          this.dateRangeChange.emit({
            startDate: this.selectedStartDate,
            endDate: this.selectedEndDate,
          });

          // Close the date picker after selection is complete
          if (this.dateRangePicker) {
            this.dateRangePicker.close();
          }

          // Trigger change detection to update the view
          this.cdr.detectChanges();
        }
      });

    // Subscribe to single date picker changes (for single day mode)
    this.singleDateControl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(date => {
        if (date && moment(date).isValid()) {
          const selectedDate = moment(date).toDate();
          this._selectedDate = selectedDate;

          // Emit date change
          this.dateChange.emit(selectedDate);

          // Close the date picker after selection
          if (this.singleDatePicker) {
            this.singleDatePicker.close();
          }

          // Trigger change detection to update the view
          this.cdr.detectChanges();
        }
      });

    // Subscribe to bid item/activity/delivery detail view requests
    this.mapService.showBidItemDetail
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        // While the polygon tool is active a map click is a vertex, not a
        // request to drill into whatever sits under it.
        if (this.spatialSummaryState?.active) {
          return;
        }
        // A geometry click inside an open drilldown for the same bid item only
        // switches the highlighted line — re-entering drilldown would re-zoom,
        // stack another temp layer and overwrite the extent to return to on close.
        const isSameBidItemDrilldown = data.type === 'bid_items'
          && !data.lightweight
          && this.detailViewMode === 'bid_item'
          && this.mapService.bidItemDetailViewActive.getValue()
          && !!data.data?.bid_item?.id
          && this.detailViewData?.data?.bid_item?.id === data.data.bid_item.id
          && this.detailViewData?.data?.siteId === data.data.siteId;

        // Set mode based on type (bid_items, activities, or deliveries)
        if (data.type === 'activities') {
          this.detailViewMode = 'activity';
        } else if (data.type === 'deliveries') {
          this.detailViewMode = 'delivery';
        } else if (data.type === 'forms') {
          this.detailViewMode = 'forms';
        } else {
          this.detailViewMode = 'bid_item';
        }

        // Transform activity data to include media property
        if (data.type === 'activities') {
          this.detailViewData = {
            ...data,
            data: this.transformActivityData(data.data)
          };
        } else if (data.type === 'deliveries') {
          // Transform delivery data to match what delivery component expects
          this.detailViewData = {
            ...data,
            data: this.transformDeliveryData(data.data)
          };
        } else if (data.type === 'forms') {
          // Pass the raw control form data straight to the real
          // app-control-forms-details component (read-only). Only normalize the
          // array fields the component assumes exist, and carry the header chips.
          this.detailViewData = {
            ...data,
            data: this.normalizeControlFormData(data.data)
          };
        } else {
          this.detailViewData = data;
        }

        const drilldownItemData = {
          ...data.data,
          _mapItemType: this.detailViewMode
        };

        if (isSameBidItemDrilldown) {
          this.mapService.selectedBidItemMarkerId.next(data?.data?.id || null);
        } else if (data.lightweight) {
          // Lightweight mode: show panel without drilldown
          this.mapService.selectedItemData.next({ ...drilldownItemData, _lightweight: true });
        } else {
          // Full drilldown mode (existing behavior)
          this.mapService.bidItemDetailViewActive.next(true);
          const markerId = data?.data?.id || null;
          this.mapService.selectedBidItemMarkerId.next(markerId);
          this.mapService.selectedItemData.next(drilldownItemData);
        }
      });
  }

  /**
   * Get count of active filters (excluding 'all')
   * Returns number of filter categories that have active selections
   */
  getActiveFilterCount(): number {
    let count = 0;

    // Count site filter if active (not 'all')
    if (!this.selectedSiteFilters.includes('all')) {
      count++;
    }

    // Count bid item filter if active (not 'all')
    if (!this.selectedBidItemFilters.includes('all')) {
      count++;
    }

    return count;
  }

  ngOnDestroy() {
    this.stationingSub?.unsubscribe();
    // Stop play animation if active
    this.stopPlayAnimation();

    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  /**
   * Calculates the timeline extent dynamically.
   * The extent encompasses:
   * - Project start date (or 1 year ago as fallback) as the minimum start
   * - Today's date as the baseline end
   * - Selected dates if they extend beyond the default range
   */
  private calculateTimelineRange(): void {
    const today = new Date();

    // Calculate default start: project start date or 1 year ago
    let defaultStart: Date;
    if (this.projectStartDate) {
      defaultStart = new Date(this.projectStartDate);
    } else {
      defaultStart = new Date(today);
      defaultStart.setFullYear(today.getFullYear() - 1);
    }

    // Timeline start is the earlier of: default start or selected start date
    if (this.selectedStartDate && this.selectedStartDate < defaultStart) {
      this.timelineStartDate = new Date(this.selectedStartDate);
    } else {
      this.timelineStartDate = defaultStart;
    }

    // Timeline end is the later of: today or selected end date
    if (this.selectedEndDate && this.selectedEndDate > today) {
      this.timelineEndDate = new Date(this.selectedEndDate);
    } else {
      this.timelineEndDate = new Date(today);
    }
  }

  onFiltersClick(origin: 'icon' | 'chip'): void {
    // Capture which specific button was clicked to open the overlay
    // This ensures the overlay stays anchored to the correct button throughout its lifecycle
    if (!this.showFiltersOverlay) {
      this.filtersOverlayOrigin = origin;
    }
    this.showFiltersOverlay = !this.showFiltersOverlay;
  }

  onCalendarClick(): void {
    // Open the appropriate date picker based on mode
    if (this.isSingleDayMode) {
      if (this.singleDatePicker) {
        this.singleDatePicker.open();
      }
    } else {
      if (this.dateRangePicker) {
        this.dateRangePicker.open();
      }
    }
  }

  onViewClick(): void {
    this.showViewOverlay = !this.showViewOverlay;
  }

  onColorBySelect(mode: string): void {
    this.selectedColorBy = mode;
    // Close the view dropdown after selection
    this.showViewOverlay = false;
  }

  onLayerToggle(layerKey: string): void {
    this.selectedLayers[layerKey] = !this.selectedLayers[layerKey];
    // Emit to parent
    this.layerChange.emit(this.selectedLayers);
  }

  /** 7.2: Stationing on the map (the project has alignments): on by default, remembered per user and project */
  onToggleStationing(): void {
    if (this.stationingProjectId) this.stationingPreference.toggle(this.stationingProjectId);
  }

  onToggleLabels(): void {
    this.labelsEnabled = !this.labelsEnabled;
    this.labelToggle.emit();
  }

  onLabelByChange(value: string): void {
    this.labelBy = value as LabelByOption;
    this.labelByChange.emit(this.labelBy);
  }

  getSiteColor(index: number): string {
    return this.siteColors[index % this.siteColors.length];
  }

  getSitesCount(): number {
    return this.filteredSitesForView.length;
  }

  onSiteHover(siteId: string | null): void {
    this.hoveredSiteId = siteId;
    this.siteHover.emit(siteId);
  }

  /**
   * Handle View By dropdown selection (Site, Bid Item, Day, Inspector)
   */
  onViewBySelect(mode: 'site' | 'bid_item' | 'day' | 'inspector'): void {
    // Clear sticky selections when changing view mode
    this.clearAllSelections();
    // Emit cleared selections to parent
    this.siteSelectionChange.emit(new Set());
    this.bidItemSelectionChange.emit(new Set());
    this.inspectorSelectionChange.emit(new Set());
    this.dateSelectionChange.emit(new Set());

    this.selectedViewBy = mode;
    // Close the view dropdown after selection
    this.showViewOverlay = false;
    // Emit the change to parent component
    this.viewByChange.emit(mode);
  }

  /**
   * Build aggregated bid items list from bidItemsWithGeometries
   * Assigns colors from the palette for display
   * Filters based on selectedBidItemIds if any are selected
   */
  private buildAggregatedBidItems(): void {
    if (!this._bidItemsWithGeometries || this._bidItemsWithGeometries.length === 0) {
      this.aggregatedBidItems = [];
      return;
    }

    // First, assign colors to ALL bid items (maintain consistent color assignment)
    const allBidItemsWithColors = this._bidItemsWithGeometries.map((item, index) => ({
      id: item.id,
      item: item.item,
      description: item.description,
      color: this.siteColors[index % this.siteColors.length]
    }));

    // Then filter based on selectedBidItemIds (empty array = show all)
    if (this._selectedBidItemIds && this._selectedBidItemIds.length > 0) {
      this.aggregatedBidItems = allBidItemsWithColors.filter(item =>
        this._selectedBidItemIds.includes(item.id)
      );
    } else {
      this.aggregatedBidItems = allBidItemsWithColors;
    }

  }

  /**
   * Build aggregated inspectors list from availableInspectors
   * Assigns colors from the palette for display
   */
  private buildAggregatedInspectors(): void {
    if (!this._availableInspectors || this._availableInspectors.length === 0) {
      this.aggregatedInspectors = [];
      return;
    }

    // Assign colors to inspectors
    this.aggregatedInspectors = this._availableInspectors.map((inspector, index) => ({
      ...inspector,
      color: inspector.color || this.siteColors[index % this.siteColors.length]
    }));

  }

  /**
   * Handle bid item hover in the list
   */
  onBidItemHover(bidItemId: string | null): void {
    this.hoveredBidItemId = bidItemId;
    this.bidItemHover.emit(bidItemId);
  }

  /**
   * Handle inspector hover in the list
   */
  onInspectorHover(inspectorId: string | null): void {
    this.inspectorHover.emit(inspectorId);
  }

  /**
   * Handle date hover (for View By Day)
   */
  onDateHover(date: string | null): void {
    this.hoveredDate = date;
    this.dateHover.emit(date);
  }

  // ==================== STICKY SELECTION HANDLERS ====================
  // Click handlers for multi-select sticky highlighting

  /**
   * Handle site click for sticky selection (multi-select toggle)
   * Clicking a site toggles its selection state
   */
  onSiteClick(siteId: string): void {
    if (this.selectedSiteIdsForHighlight.has(siteId)) {
      this.selectedSiteIdsForHighlight.delete(siteId);
    } else {
      this.selectedSiteIdsForHighlight.add(siteId);
    }
    this.siteSelectionChange.emit(new Set(this.selectedSiteIdsForHighlight));
  }

  /**
   * Handle bid item click for sticky selection (multi-select toggle)
   * Clicking a bid item toggles its selection state
   */
  onBidItemClick(bidItemId: string): void {
    if (this.selectedBidItemIdsForHighlight.has(bidItemId)) {
      this.selectedBidItemIdsForHighlight.delete(bidItemId);
    } else {
      this.selectedBidItemIdsForHighlight.add(bidItemId);
    }
    this.bidItemSelectionChange.emit(new Set(this.selectedBidItemIdsForHighlight));
  }

  /**
   * Handle inspector click for sticky selection (multi-select toggle)
   * Clicking an inspector toggles its selection state
   */
  onInspectorClick(inspectorId: string): void {
    if (this.selectedInspectorIdsForHighlight.has(inspectorId)) {
      this.selectedInspectorIdsForHighlight.delete(inspectorId);
    } else {
      this.selectedInspectorIdsForHighlight.add(inspectorId);
    }
    this.inspectorSelectionChange.emit(new Set(this.selectedInspectorIdsForHighlight));
  }

  /**
   * Handle date click for sticky selection (multi-select toggle)
   * Clicking a date toggles its selection state
   */
  onDateClick(date: string): void {
    if (this.selectedDatesForHighlight.has(date)) {
      this.selectedDatesForHighlight.delete(date);
    } else {
      this.selectedDatesForHighlight.add(date);
    }
    this.dateSelectionChange.emit(new Set(this.selectedDatesForHighlight));
  }

  /**
   * Check if a site is selected for sticky highlighting
   */
  isSiteSelected(siteId: string): boolean {
    return this.selectedSiteIdsForHighlight.has(siteId);
  }

  /**
   * Check if a bid item is selected for sticky highlighting
   */
  isBidItemSelected(bidItemId: string): boolean {
    return this.selectedBidItemIdsForHighlight.has(bidItemId);
  }

  /**
   * Check if an inspector is selected for sticky highlighting
   */
  isInspectorSelected(inspectorId: string): boolean {
    return this.selectedInspectorIdsForHighlight.has(inspectorId);
  }

  /**
   * Check if a date is selected for sticky highlighting
   */
  isDateSelected(date: string): boolean {
    return this.selectedDatesForHighlight.has(date);
  }

  /**
   * Clear all selections when view mode changes
   */
  private clearAllSelections(): void {
    this.selectedSiteIdsForHighlight.clear();
    this.selectedBidItemIdsForHighlight.clear();
    this.selectedInspectorIdsForHighlight.clear();
    this.selectedDatesForHighlight.clear();
  }

  /**
   * Clear site selections (public method for clear button)
   */
  clearSiteSelections(): void {
    this.selectedSiteIdsForHighlight.clear();
    this.siteSelectionChange.emit(new Set());
  }

  /**
   * Clear bid item selections (public method for clear button)
   */
  clearBidItemSelections(): void {
    this.selectedBidItemIdsForHighlight.clear();
    this.bidItemSelectionChange.emit(new Set());
  }

  /**
   * Clear inspector selections (public method for clear button)
   */
  clearInspectorSelections(): void {
    this.selectedInspectorIdsForHighlight.clear();
    this.inspectorSelectionChange.emit(new Set());
  }

  /**
   * Clear date selections (public method for clear button)
   */
  clearDateSelections(): void {
    this.selectedDatesForHighlight.clear();
    this.dateSelectionChange.emit(new Set());
  }

  // ==================== END STICKY SELECTION HANDLERS ====================

  /**
   * Get color for a bid item by its ID
   */
  getBidItemColor(bidItemId: string): string {
    const item = this.aggregatedBidItems.find(bi => bi.id === bidItemId);
    return item?.color || this.siteColors[0];
  }

  /**
   * Get total count of unique bid items
   */
  getBidItemsCount(): number {
    return this.aggregatedBidItems.length;
  }

  /**
   * Get total count of unique inspectors
   */
  getInspectorsCount(): number {
    return this.aggregatedInspectors.length;
  }

  /**
   * Build aggregated dates list from filteredSites.daily_reports
   * Extracts unique dates from all daily reports and assigns colors
   */
  private buildAggregatedDates(): void {
    const uniqueDates = new Set<string>();

    // Extract dates from all daily_reports in filteredSites
    (this.filteredSitesForView || []).forEach(site => {
      if (site.daily_reports && Array.isArray(site.daily_reports)) {
        site.daily_reports.forEach((report: any) => {
          const reportDate = report.date || report.report_date;
          if (reportDate) {
            // Normalize date to YYYY-MM-DD format
            const normalizedDate = reportDate.split('T')[0];
            uniqueDates.add(normalizedDate);
          }
        });
      }
    });

    // Sort dates chronologically and assign colors
    const sortedDates = Array.from(uniqueDates).sort();
    this.aggregatedDates = sortedDates.map((date, index) => ({
      date: date,
      displayDate: this.formatDateForDisplay(date),
      color: this.siteColors[index % this.siteColors.length]
    }));

  }

  /**
   * Format date for display in the sidenav (e.g., "Nov 17, 2025")
   */
  private formatDateForDisplay(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  /**
   * Get total count of unique dates
   */
  getDatesCount(): number {
    return this.aggregatedDates.length;
  }

  toggleFilter(type: 'site' | 'bidItem' | 'payApp', itemId: string): void {
    let selectedFilters: string[];

    switch (type) {
      case 'site':
        selectedFilters = this.selectedSiteFilters;
        break;
      case 'bidItem':
        selectedFilters = this.selectedBidItemFilters;
        break;
      case 'payApp':
        selectedFilters = this.selectedPayAppFilters;
        break;
    }

    if (itemId === 'all') {
      // If clicking "All", clear all other selections
      selectedFilters.length = 0;
      selectedFilters.push('all');
    } else {
      // Remove "all" if it's selected
      const allIndex = selectedFilters.indexOf('all');
      if (allIndex > -1) {
        selectedFilters.splice(allIndex, 1);
      }

      // Toggle the item
      const index = selectedFilters.indexOf(itemId);
      if (index > -1) {
        selectedFilters.splice(index, 1);
      } else {
        selectedFilters.push(itemId);
      }

      // If no items selected, select "all"
      if (selectedFilters.length === 0) {
        selectedFilters.push('all');
      }
    }

    // Emit filter changes based on type
    if (type === 'bidItem') {
      this.emitBidItemFilterChange();
    } else if (type === 'site') {
      this.emitSiteFilterChange();
    }
  }

  private emitBidItemFilterChange(): void {
    // If "all" is selected, emit empty array (means show all)
    if (this.selectedBidItemFilters.includes('all')) {
      this.bidItemFilterChange.emit([]);
    } else {
      // Emit string IDs (bid items use base58 IDs, not numeric)
      const selectedIds = this.selectedBidItemFilters.filter(id => id !== 'all');
      this.bidItemFilterChange.emit(selectedIds);
    }
  }

  private emitSiteFilterChange(): void {
    // If "all" is selected, emit empty array (means show all)
    if (this.selectedSiteFilters.includes('all')) {
      this.siteFilterChange.emit([]);
    } else {
      // Emit string IDs (site IDs are base58)
      const selectedIds = this.selectedSiteFilters.filter(id => id !== 'all');
      this.siteFilterChange.emit(selectedIds);
    }
  }

  isSiteFilterChecked(itemId: string): boolean {
    return this.selectedSiteFilters.includes(itemId);
  }

  isBidItemFilterChecked(itemId: string): boolean {
    return this.selectedBidItemFilters.includes(itemId);
  }

  isPayAppFilterChecked(itemId: string): boolean {
    return this.selectedPayAppFilters.includes(itemId);
  }

  toggleTypeFilter(typeId: string): void {
    // Forms group toggles behave exactly like the base types: present = shown,
    // absent = hidden. No special-casing.
    const index = this.selectedTypeFilters.indexOf(typeId);
    if (index > -1) {
      this.selectedTypeFilters.splice(index, 1);
    } else {
      this.selectedTypeFilters.push(typeId);
    }

    // Emit type filter change to parent
    this.typeFilterChange.emit([...this.selectedTypeFilters]);
  }

  isTypeFilterChecked(typeId: string): boolean {
    return this.selectedTypeFilters.includes(typeId);
  }

  getFilteredSites(): FilterItem[] {
    if (!this.debouncedSearchQuery.trim()) {
      return this.siteFilters;
    }
    const query = this.debouncedSearchQuery.toLowerCase();
    return this.siteFilters.filter(item =>
      item.label.toLowerCase().includes(query)
    );
  }

  getFilteredBidItems(): FilterItem[] {
    if (!this.debouncedSearchQuery.trim()) {
      return this.bidItemFilters;
    }
    const query = this.debouncedSearchQuery.toLowerCase();
    return this.bidItemFilters.filter(item =>
      item.item?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  }

  getFilteredPayApps(): FilterItem[] {
    if (!this.debouncedSearchQuery.trim()) {
      return this.payAppFilters;
    }
    const query = this.debouncedSearchQuery.toLowerCase();
    return this.payAppFilters.filter(item =>
      item.label.toLowerCase().includes(query)
    );
  }

  onSearchInput(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(query);
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.searchChange.emit(query);
  }

  onDateFilterChange(range: any): void {
    this.dateRangeChange.emit(range);
  }

  onViewModeSelect(mode: string): void {
    this.selectedViewMode = mode;
    this.viewModeChange.emit(mode);
  }

  // Timeline slider methods
  getFormattedDateRange(): string {
    // Check if both dates are in the same year
    const sameYear = this.selectedStartDate.getFullYear() === this.selectedEndDate.getFullYear();

    if (sameYear) {
      // Same year: "Sep 5 - Sep 20, 2025"
      const startOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      const endOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
      const startStr = this.selectedStartDate.toLocaleDateString('en-US', startOptions);
      const endStr = this.selectedEndDate.toLocaleDateString('en-US', endOptions);
      return `${startStr} - ${endStr}`;
    } else {
      // Different years: "Dec 25, 2024 - Jan 5, 2025"
      const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
      const startStr = this.selectedStartDate.toLocaleDateString('en-US', options);
      const endStr = this.selectedEndDate.toLocaleDateString('en-US', options);
      return `${startStr} - ${endStr}`;
    }
  }

  getSliderStartPercent(): number {
    const totalRange = this.timelineEndDate.getTime() - this.timelineStartDate.getTime();
    const startOffset = this.selectedStartDate.getTime() - this.timelineStartDate.getTime();
    return (startOffset / totalRange) * 100;
  }

  getSliderEndPercent(): number {
    const totalRange = this.timelineEndDate.getTime() - this.timelineStartDate.getTime();
    const endOffset = this.selectedEndDate.getTime() - this.timelineStartDate.getTime();
    return (endOffset / totalRange) * 100;
  }

  getSliderWidthPercent(): number {
    return this.getSliderEndPercent() - this.getSliderStartPercent();
  }

  getTimelineMonths(): string[] {
    if (!this.timelineStartDate || !this.timelineEndDate) {
      return [];
    }

    const targetLabels = 7; // Design calls for 7 labels

    // Calculate total months in extent
    let totalMonths = 0;
    const temp = new Date(this.timelineStartDate);
    while (temp <= this.timelineEndDate) {
      totalMonths++;
      temp.setMonth(temp.getMonth() + 1);
    }

    // If we have fewer months than target labels, just show all months
    if (totalMonths <= targetLabels) {
      const months: string[] = [];
      const current = new Date(this.timelineStartDate);
      while (current <= this.timelineEndDate) {
        months.push(current.toLocaleDateString('en-US', { month: 'short' }));
        current.setMonth(current.getMonth() + 1);
      }
      return months;
    }

    // For more months than labels, distribute evenly across the timeline
    // Always include first and last month, distribute remaining labels evenly between them
    const months: string[] = [];

    for (let i = 0; i < targetLabels; i++) {
      // Calculate which month index this label should represent
      // i=0 -> first month, i=targetLabels-1 -> last month
      const monthIndex = Math.round((i / (targetLabels - 1)) * (totalMonths - 1));

      const labelDate = new Date(this.timelineStartDate);
      labelDate.setMonth(labelDate.getMonth() + monthIndex);

      months.push(labelDate.toLocaleDateString('en-US', { month: 'short' }));
    }

    return months;
  }

  onSliderHandleMouseDown(event: MouseEvent | TouchEvent, handle: 'start' | 'end'): void {
    event.preventDefault();
    event.stopPropagation(); // Prevent range drag when clicking handles
    this.isDragging = true;
    this.dragHandle = handle;
    this.mapService.isSliderDragging.next(true); // Broadcast drag start

    const onMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!this.isDragging) return;
      this.updateSliderPosition(e);
    };

    const onMouseUp = () => {
      this.isDragging = false;
      this.dragHandle = null;
      this.mapService.isSliderDragging.next(false); // Broadcast drag end
      document.removeEventListener('mousemove', onMouseMove as any);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onMouseMove as any);
      document.removeEventListener('touchend', onMouseUp);

      // Emit final update (force, ignore throttle)
      this.lastEmitTime = 0;
      this.emitDateRangeWithoutNavigation();

      // Update query params when slider drag ends
      this.updateQueryParams();
    };

    document.addEventListener('mousemove', onMouseMove as any);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onMouseMove as any);
    document.addEventListener('touchend', onMouseUp);
  }

  /**
   * Handle clicking/dragging on the selected range bar to move the entire window
   */
  onSliderRangeMouseDown(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragHandle = 'range';
    this.mapService.isSliderDragging.next(true); // Broadcast drag start

    const trackElement = document.querySelector('.timeline-slider-track') as HTMLElement;
    if (!trackElement) return;

    const rect = trackElement.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    this.dragStartX = clientX;
    this.dragStartPercent = ((clientX - rect.left) / rect.width) * 100;

    const onMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!this.isDragging) return;
      this.updateSliderPosition(e);
    };

    const onMouseUp = () => {
      this.isDragging = false;
      this.dragHandle = null;
      this.mapService.isSliderDragging.next(false); // Broadcast drag end
      this.dragStartX = 0;
      this.dragStartPercent = 0;
      document.removeEventListener('mousemove', onMouseMove as any);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onMouseMove as any);
      document.removeEventListener('touchend', onMouseUp);

      // Emit final update (force, ignore throttle)
      this.lastEmitTime = 0;
      this.emitDateRangeWithoutNavigation();

      // Update query params when slider drag ends
      this.updateQueryParams();
    };

    document.addEventListener('mousemove', onMouseMove as any);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onMouseMove as any);
    document.addEventListener('touchend', onMouseUp);
  }

  private updateSliderPosition(event: MouseEvent | TouchEvent): void {
    const trackElement = document.querySelector('.timeline-slider-track') as HTMLElement;
    if (!trackElement) return;

    const rect = trackElement.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));

    const totalRange = this.timelineEndDate.getTime() - this.timelineStartDate.getTime();

    if (this.dragHandle === 'start') {
      const newTime = this.timelineStartDate.getTime() + (percent / 100) * totalRange;
      const newDate = new Date(newTime);
      if (newDate < this.selectedEndDate) {
        this.selectedStartDate = newDate;
        this.emitDateRangeWithoutNavigation(); // Emit during drag without navigation
      }
    } else if (this.dragHandle === 'end') {
      const newTime = this.timelineStartDate.getTime() + (percent / 100) * totalRange;
      const newDate = new Date(newTime);
      if (newDate > this.selectedStartDate) {
        this.selectedEndDate = newDate;
        this.emitDateRangeWithoutNavigation(); // Emit during drag without navigation
      }
    } else if (this.dragHandle === 'range') {
      // Move entire range while preserving duration
      const selectionDuration = this.selectedEndDate.getTime() - this.selectedStartDate.getTime();

      // Calculate the delta movement in percentage
      const deltaPercent = percent - this.dragStartPercent;
      const deltaTime = (deltaPercent / 100) * totalRange;

      // Calculate new start and end times
      let newStartTime = this.selectedStartDate.getTime() + deltaTime;
      let newEndTime = this.selectedEndDate.getTime() + deltaTime;

      // Constrain to timeline bounds
      if (newStartTime < this.timelineStartDate.getTime()) {
        newStartTime = this.timelineStartDate.getTime();
        newEndTime = newStartTime + selectionDuration;
      } else if (newEndTime > this.timelineEndDate.getTime()) {
        newEndTime = this.timelineEndDate.getTime();
        newStartTime = newEndTime - selectionDuration;
      }

      this.selectedStartDate = new Date(newStartTime);
      this.selectedEndDate = new Date(newEndTime);

      // Update drag start position for next move event
      this.dragStartPercent = percent;

      this.emitDateRangeWithoutNavigation(); // Emit during drag without navigation
    }
  }

  /**
   * Emit date range changes without navigating (for use during drag)
   * Throttled to avoid too many updates per second
   */
  private emitDateRangeWithoutNavigation(): void {
    const now = Date.now();
    if (now - this.lastEmitTime >= this.EMIT_THROTTLE_MS) {
      this.dateRangeChange.emit({
        start: this.selectedStartDate,
        end: this.selectedEndDate
      });
      this.lastEmitTime = now;
    }
  }

  /**
   * Move selection window backward by the current selection range
   */
  onTimelinePrevious(): void {
    // Compare dates only (ignore time) to check if we're at the start
    const selectedStartDay = new Date(this.selectedStartDate.getFullYear(), this.selectedStartDate.getMonth(), this.selectedStartDate.getDate());
    const timelineStartDay = new Date(this.timelineStartDate.getFullYear(), this.timelineStartDate.getMonth(), this.timelineStartDate.getDate());

    // If already at the start (comparing dates only), do nothing
    if (selectedStartDay.getTime() <= timelineStartDay.getTime()) {
      return;
    }

    const rangeDays = Math.floor((this.selectedEndDate.getTime() - this.selectedStartDate.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate new selection dates
    const newStart = new Date(this.selectedStartDate.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    const newEnd = new Date(this.selectedEndDate.getTime() - rangeDays * 24 * 60 * 60 * 1000);

    // Ensure we don't go before timeline extent start
    if (newStart < this.timelineStartDate) {
      this.selectedStartDate = new Date(this.timelineStartDate);
      this.selectedEndDate = new Date(this.selectedStartDate.getTime() + rangeDays * 24 * 60 * 60 * 1000);
    } else {
      this.selectedStartDate = newStart;
      this.selectedEndDate = newEnd;
    }

    this.updateQueryParams();
  }

  /**
   * Move selection window forward by the current selection range
   */
  onTimelineNext(): void {
    // Compare dates only (ignore time) to check if we're at the end
    const selectedEndDay = new Date(this.selectedEndDate.getFullYear(), this.selectedEndDate.getMonth(), this.selectedEndDate.getDate());
    const timelineEndDay = new Date(this.timelineEndDate.getFullYear(), this.timelineEndDate.getMonth(), this.timelineEndDate.getDate());

    // If already at the end (comparing dates only), do nothing
    if (selectedEndDay.getTime() >= timelineEndDay.getTime()) {
      return;
    }

    const rangeDays = Math.floor((this.selectedEndDate.getTime() - this.selectedStartDate.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate new selection dates
    const newStart = new Date(this.selectedStartDate.getTime() + rangeDays * 24 * 60 * 60 * 1000);
    const newEnd = new Date(this.selectedEndDate.getTime() + rangeDays * 24 * 60 * 60 * 1000);

    // Ensure we don't go past timeline extent end
    if (newEnd > this.timelineEndDate) {
      this.selectedEndDate = new Date(this.timelineEndDate);
      this.selectedStartDate = new Date(this.selectedEndDate.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    } else {
      this.selectedStartDate = newStart;
      this.selectedEndDate = newEnd;
    }

    this.updateQueryParams();
  }

  /**
   * Toggle play/pause for timeline animation
   */
  onTimelinePlay(): void {
    if (this.isPlaying) {
      this.stopPlayAnimation();
    } else {
      this.startPlayAnimation();
    }
  }

  /**
   * Cycle through playback speeds: 0.5x → 1x → 2x → 4x → 0.5x
   */
  onSpeedCycle(): void {
    // Move to next speed
    this.currentSpeedIndex = (this.currentSpeedIndex + 1) % this.SPEED_OPTIONS.length;
    this.playbackSpeed = this.SPEED_OPTIONS[this.currentSpeedIndex];

    // If currently playing, restart the animation with new speed
    if (this.isPlaying) {
      this.stopPlayAnimation();
      this.startPlayAnimation();
    }
  }

  /**
   * Start automatic timeline advancement
   */
  private startPlayAnimation(): void {
    this.isPlaying = true;
    this.playStepCounter = 0; // Reset counter

    // Broadcast playing state to prevent map zoom during playback
    this.mapService.isPlaying.next(true);

    // Calculate interval based on speed multiplier (higher speed = shorter interval)
    const intervalMs = this.BASE_PLAY_INTERVAL_MS / this.playbackSpeed;

    this.playIntervalId = setInterval(() => {
      this.advanceTimeline();
    }, intervalMs);
  }

  /**
   * Stop automatic timeline advancement
   */
  private stopPlayAnimation(): void {
    this.isPlaying = false;

    // Broadcast playing state stopped to allow map zoom again
    this.mapService.isPlaying.next(false);

    if (this.playIntervalId) {
      clearInterval(this.playIntervalId);
      this.playIntervalId = null;
    }

    // Emit final data fetch when stopping to ensure map is up to date
    this.emitDateRangeWithoutNavigation();
  }

  /**
   * Advance the timeline selection window forward by one step
   */
  private advanceTimeline(): void {
    const stepMs = this.PLAY_STEP_DAYS * 24 * 60 * 60 * 1000;
    const rangeDuration = this.selectedEndDate.getTime() - this.selectedStartDate.getTime();

    // Calculate new dates
    const newStartTime = this.selectedStartDate.getTime() + stepMs;
    const newEndTime = this.selectedEndDate.getTime() + stepMs;

    // Check if we've reached the end of the timeline
    if (newEndTime > this.timelineEndDate.getTime()) {
      // Stop at the end and update final position
      this.stopPlayAnimation();
      this.updateQueryParams();
      return;
    }

    // Update selection (visual update happens immediately via Angular binding + CSS transition)
    this.selectedStartDate = new Date(newStartTime);
    this.selectedEndDate = new Date(newEndTime);

    // Increment counter
    this.playStepCounter++;

    // Only fetch data every N steps to avoid overwhelming the system
    // The CSS transition (300ms) will smooth out the visual movement
    if (this.playStepCounter % this.DATA_FETCH_INTERVAL === 0) {
      // Fetch data at intervals for better performance
      this.emitDateRangeWithoutNavigation();
    }
    // Note: Visual slider position updates every step automatically via Angular bindings
  }

  // Update query params to sync with calendar picker
  private updateQueryParams(): void {
    const startDate = moment(this.selectedStartDate).format('YYYY-MM-DD');
    const endDate = moment(this.selectedEndDate).format('YYYY-MM-DD');

    this.router.navigate([], {
      queryParams: {
        startDate,
        endDate,
      },
      queryParamsHandling: 'merge',
    });

    // Also emit change event for parent component
    this.dateRangeChange.emit({
      startDate: this.selectedStartDate,
      endDate: this.selectedEndDate
    });
  }

  /**
   * Close detail view and return to normal sidenav mode
   */
  closeDetailView(): void {
    this.detailViewMode = 'none';
    this.detailViewData = null;
    // Broadcast detail view closed for map fade effect
    this.mapService.bidItemDetailViewActive.next(false);
    this.mapService.selectedBidItemMarkerId.next(null);
    this.mapService.selectedItemData.next(null);
    // Clear hover and selection highlight state
    this.mapService.hoveredGpsFeatureIndex.next(null);
    this.mapService.hoveredLineItemId.next(null);
    this.mapService.selectedGpsFeatureIndex.next(null);
    this.mapService.selectedGpsLineItemId.next(null);
    this.mapService.openLineItemId.next(null);
  }

  /**
   * Transform activity data to match the format expected by activity-item-details component
   * Converts pictures array to media array with fileType property
   */
  private transformActivityData(activityData: any): any {
    if (!activityData) {
      return activityData;
    }

    // Transform pictures to media format (activity component expects media, not pictures)
    const media = (activityData.pictures || []).map(pic => ({
      ...pic,
      fileType: this.getFileTypeFromMime(pic.mime)
    }));

    // Add videos if present
    if (activityData.videos && activityData.videos.length > 0) {
      const videos = activityData.videos.map(vid => ({
        ...vid,
        fileType: 'video'
      }));
      media.push(...videos);
    }

    return {
      ...activityData,
      media: media
    };
  }

  /**
   * Transform delivery data to match what delivery-details component expects
   * Similar to activity transform but for deliveries
   */
  private transformDeliveryData(deliveryData: any): any {
    if (!deliveryData) {
      return deliveryData;
    }

    // Determine subType based on whether it has bid_item and/or material
    let subType = 'onlyBidItem'; // default
    if (deliveryData.bid_item && deliveryData.material) {
      subType = 'bidItemWithMaterials';
    } else if (deliveryData.material && !deliveryData.bid_item) {
      subType = 'onlyMaterial';
    } else if (deliveryData.bid_item && !deliveryData.material) {
      subType = 'onlyBidItem';
    }
    // The delivery component expects properties directly on data object
    // Looking at delivery-details.component.ts:
    // - Line 227: Uses this.data.id as parent form key
    // - Line 256-279: createLineItemForm expects quantity, comment, pictures, videos, fieldValues, latitude, longitude directly on data
    // - Line 60-64: Uses data.subType and data.initialState
    // Build heading and subheading based on what's available
    let heading = 'Delivery';
    let uom = '';
    if (deliveryData.bid_item) {
      if (deliveryData.bid_item.item) {
        heading = deliveryData.bid_item.item; // Just the item number, no prefix
      }
      uom = deliveryData.bid_item.uom || '';
    }
    if (deliveryData.material) {
      heading = deliveryData.material.name || 'Material Delivery';
      uom = deliveryData.material.uom || uom;
    }

    // Add fileType to pictures and videos
    const pictures = (deliveryData.pictures || []).map((pic: any) => ({
      ...pic,
      fileType: 'picture'
    }));
    const videos = (deliveryData.videos || []).map((vid: any) => ({
      ...vid,
      fileType: 'video'
    }));

    const transformed = {
      id: deliveryData.id,
      subType: subType,
      heading: heading,
      subheading: deliveryData.bid_item?.description || undefined,
      // Carry context for the drilldown header chips (site + date), matching the
      // bid-item panel. These are dropped otherwise since we rebuild the object.
      siteName: deliveryData.siteName || deliveryData.site?.name || null,
      reportDate: deliveryData.reportDate || deliveryData.report_date || null,
      uom: uom,
      quantity: deliveryData.quantity,
      comment: deliveryData.comment,
      // Pictures and videos need to be arrays with fileType property
      pictures: pictures,
      videos: videos,
      // Map field_values to fieldValues format the component expects
      fieldValues: (deliveryData.field_values || []).map((fv: any) => ({
        id: fv.field?.id || fv.id,
        value: fv.value
      })),
      latitude: deliveryData.latitude || null,
      longitude: deliveryData.longitude || null,
      location: deliveryData.location || null,
      station_range: deliveryData.station_range || null,
      // initialState is used for bid_item_id lookup (line 61)
      initialState: {
        id: deliveryData.id,
        bid_item_id: deliveryData.bid_item?.id || null,
        material_id: deliveryData.material?.id || null,
        quantity: deliveryData.quantity,
        comment: deliveryData.comment,
        latitude: deliveryData.latitude || null,
        longitude: deliveryData.longitude || null,
        location: deliveryData.location || null,
        station_range: deliveryData.station_range || null,
        received: deliveryData.received,
        used: deliveryData.used,
        pictures: pictures,
        tickets: deliveryData.tickets || [],
        videos: videos,
        field_values: deliveryData.field_values || [],
        bid_item: deliveryData.bid_item || null,
        material: deliveryData.material || null
      },
      // Materials array is for bidItemWithMaterials subType (line 230-232)
      materials: []
    };

    return transformed;
  }

  /**
   * Normalize a control form marker's data for the read-only
   * app-control-forms-details component. The real component reads
   * data.control_form / field_values / lines / station_range / signatures /
   * pictures / videos and assumes the array fields exist — guarantee that, and
   * carry siteName / reportDate for the panel's site + date chips.
   */
  private normalizeControlFormData(formData: any): any {
    if (!formData) {
      return formData;
    }
    return {
      ...formData,
      control_form: formData.control_form,
      field_values: formData.field_values || [],
      lines: formData.lines || [],
      signatures: formData.signatures || [],
      pictures: formData.pictures || [],
      videos: formData.videos || [],
      station_range: formData.station_range || null,
      // The forms detail header shows the form name + site/date chips.
      name: formData.control_form?.name || 'Form',
      siteName: formData.siteName || formData.site?.name || null,
      reportDate: formData.reportDate || formData.report_date || null,
    };
  }

  /**
   * Convert MIME type to fileType expected by pictures-section component
   */
  private getFileTypeFromMime(mime: string): string {
    if (!mime) return 'picture';

    if (mime.startsWith('image/')) {
      return 'picture';
    } else if (mime.startsWith('video/')) {
      return 'video';
    } else if (mime.startsWith('application/') || mime.startsWith('text/')) {
      return 'document';
    }

    return 'picture';
  }
}
